import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { getAdminUrl } from "@/lib/host";

const AuthCallback = () => {
  const loc = useLocation();
  const nav = useNavigate();
  const { setSession } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");
  const [otpStep, setOtpStep] = useState(null);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const adminHost = false;

  const redirectToAdmin = () => {
    if (adminHost) {
      nav("/admin", { replace: true });
      return;
    }
    window.location.assign(getAdminUrl());
  };

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = loc.hash || window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError("No sign-in session found. Please try signing in again.");
      return;
    }
    const sid = decodeURIComponent(match[1]);
    const consentAccepted = window.sessionStorage.getItem("eira-account-consent") === "accepted";
    // Clear the hash immediately so the token isn't left in the URL
    window.history.replaceState(null, "", loc.pathname);
    api.post("/auth/session", { session_id: sid, consent_accepted: consentAccepted })
      .then(({ data }) => {
        window.sessionStorage.removeItem("eira-account-consent");
        if (data.otp_required) {
          setOtpStep({ pendingId: data.pending_id, sentTo: data.otp_sent_to });
          toast.success(`A 6-digit code was sent to ${data.otp_sent_to}`);
        } else {
          setSession(data);
          toast.success(`Welcome, ${data.user?.name || "back"}!`);
          if (data.user?.role === "admin") {
            redirectToAdmin();
          } else {
            nav("/", { replace: true });
          }
        }
      })
      .catch((e) => setError(formatError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { pending_id: otpStep.pendingId, otp: otp.trim() });
      setSession(data);
      toast.success("Admin session verified");
      redirectToAdmin();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  if (error) {
    return (
      <div className="max-w-md mx-auto py-24 px-4 text-center">
        <h1 className="font-serif text-3xl mb-2">Sign-in failed</h1>
        <p className="text-sm text-[#64748B] mb-6">{error}</p>
        <button onClick={() => nav("/login")} className="bg-[#2D5A47] hover:bg-[#214435] text-white px-6 py-3 rounded-lg font-semibold" data-testid="auth-back-login">Back to sign in</button>
      </div>
    );
  }

  if (otpStep) {
    return (
      <div className="max-w-md mx-auto py-16 px-4">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[#EBF2EE] text-[#2D5A47] flex items-center justify-center mx-auto mb-3">
            <ShieldCheck size={26} />
          </div>
          <h1 className="font-serif text-4xl">One more step</h1>
          <p className="text-sm text-[#64748B] mt-2">We sent a 6-digit code to <span className="font-semibold text-[#1E2A24]">{otpStep.sentTo}</span></p>
        </div>
        <form onSubmit={verifyOtp} className="bg-white border border-[#E2DCD2] rounded-xl p-6 space-y-4">
          <input
            data-testid="otp-input"
            autoFocus
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            placeholder="123456"
            className="w-full border border-[#E2DCD2] rounded-lg px-3 py-4 text-center font-mono text-2xl tracking-[10px] outline-none focus:border-[#2D5A47]"
          />
          <button type="submit" disabled={loading || otp.length !== 6} className="w-full bg-[#2D5A47] hover:bg-[#214435] disabled:opacity-50 text-white py-3 rounded-lg font-semibold" data-testid="otp-submit">
            {loading ? "Verifying…" : "Verify & sign in"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-24 px-4 text-center" data-testid="auth-callback-loading">
      <Loader2 size={40} className="animate-spin mx-auto mb-4 text-[#2D5A47]" />
      <div className="font-serif text-2xl mb-1">Completing your Google sign-in…</div>
      <p className="text-sm text-[#64748B]">Just a moment while we verify your account.</p>
    </div>
  );
};

export default AuthCallback;
